"use client";

import React, { useState } from "react";
import { Info, CircleCheck, ArrowRight } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const stages = [{
  title: "Inspection",
  subtitle: "20-Point Assessment",
  description: "We coordinate a comprehensive evaluation by a trusted local professional who measures moisture levels, checks for wood rot, and identifies air quality hazards like mold or radon.",
  expectation: "Expect a detailed report from the referred specialist, including photos of problem areas and a clear explanation of how they affect your home's structural health."
}, {
  title: "Strategy",
  subtitle: "Custom Engineering",
  description: "We work with certified engineers to create a custom encapsulation plan that you can share with your chosen professional. This includes vapor barrier thickness and drainage logistics.",
  expectation: "You’ll receive a professional blueprint you can give to your selected contractor that addresses humidity control and long-term moisture prevention."
}, {
  title: "Proposal",
  subtitle: "Fixed-Price Quote",
  description: "We provide a transparent, fixed-price quote you can forward to any qualified crawl-space contractor, ensuring you know the exact investment required.",
  expectation: "You’ll have an itemized quote ready for the contractor you choose, with no hidden fees and a clear scope of work."
}, {
  title: "Installation",
  subtitle: "Expert Execution",
  description: "We match you with a trusted local crew who will handle the installation, ensuring they follow best practices to create a clean, dry environment.",
  expectation: "Expect a clean work area and a professional team you’ve selected, with immediate improvements in indoor air quality."
}, {
  title: "Protection",
  subtitle: "WARRANTY & SUPPORT",
  description: "We arrange a final walkthrough with the contractor and activate a transferable warranty on your behalf once the project meets our quality standards.",
  expectation: "Enjoy peace of mind knowing you have a trusted local partner and ongoing support for your healthy home foundation."
}];
export default function TheProjectRoadmap() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const [activeStep, setActiveStep] = useState<number | null>(0);
  return <section data-section-id="4072" id="the-path-to-a-healthy-home" ref={ref} className={cn("bg-background py-20 md:py-32 relative overflow-hidden transition-opacity duration-700 dark", isVisible ? "opacity-100" : "opacity-0")}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto mb-16 text-center">
          <h2 className="text-4xl md:text-6xl font-sans font-bold text-foreground mb-6 tracking-tighter">
            Your Path to a Healthy Home, Powered by Trusted Local Professionals
          </h2>
          <p className="text-xl text-muted-foreground leading-relaxed">We connect you with crawl-space experts who follow a proven process to ensure your home remains dry, protected, and efficient.</p>
        </div>

        <div className="relative max-w-4xl mx-auto">
          {}
          <div className="absolute left-[19px] md:left-[23px] top-4 bottom-4 w-px bg-border" />

          <div className="space-y-8 md:space-y-12">
            {stages.map((stage, index) => {
            const isActive = activeStep === index;
            return <div key={index} data-index={index} className={cn("relative pl-16 md:pl-20 transition-all duration-500 ease-in-out group cursor-pointer", isActive ? "opacity-100" : "opacity-60 hover:opacity-90")} onClick={() => setActiveStep(index)} onMouseEnter={() => setActiveStep(index)}>
                  {}
                  <div className={cn("absolute left-0 top-1 md:top-1.5 flex items-center justify-center transition-all duration-500 z-10", isActive ? "scale-110" : "scale-100")}>
                    <div className={cn("w-10 h-10 md:w-12 md:h-12 rounded-full border-2 flex items-center justify-center bg-background transition-colors duration-300", isActive ? "border-secondary shadow-[0_0_20px_rgba(234,88,12,0.4)]" : "border-muted-foreground/30 group-hover:border-primary/60")}>
                      <div className={cn("w-3 h-3 md:w-4 md:h-4 rounded-full transition-colors duration-300", isActive ? "bg-secondary" : "bg-muted-foreground/40")} />
                    </div>
                  </div>

                  {}
                  <div className="flex flex-col items-start">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className={cn("text-xl md:text-3xl font-bold transition-colors duration-300 tracking-tight", isActive ? "text-foreground" : "text-muted-foreground")}>
                        {stage.title}
                      </h3>
                      {isActive && <span className="hidden sm:inline-block px-2 py-0.5 text-xs font-mono font-medium text-secondary bg-secondary/10 rounded-sm animate-in fade-in zoom-in duration-300">
                          STAGE 0{index + 1}
                        </span>}
                    </div>
                    
                    <h4 className="text-xs md:text-sm font-mono font-medium text-muted-foreground mb-3 uppercase tracking-widest">
                      {stage.subtitle}
                    </h4>

                    <div className={cn("grid transition-all duration-500 ease-in-out overflow-hidden", isActive ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0 mt-0")}>
                      <div className="min-h-0">
                        

                        <div className="flex items-center gap-2 mt-2">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              
                            </TooltipTrigger>
                            <TooltipContent side="right" className="max-w-xs bg-card text-card-foreground border-border p-4 shadow-2xl">
                              <p className="text-sm leading-relaxed">
                                {stage.expectation}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>

        <div className="mt-20 text-center">
          <Link to="#">
            <Button size="lg" className="bg-secondary hover:bg-secondary/90 text-white px-10 py-7 text-xl font-bold rounded-sm shadow-xl hover:shadow-2xl transition-all active:translate-y-1">
              Schedule Free Inspection
              <ArrowRight className="ml-2 w-6 h-6" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}